import { Directive, ViewRef, HostListener, OnDestroy, TemplateRef, ViewContainerRef, Input } from '@angular/core';
import { ScreenSizeService } from '@fgb/core';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

export enum ScreenType {
  'Mobile',
  'Desktop',
}

@Directive({
  selector: '[ifScreenSize]',
})
/** Creates the view only for the selected window size.
 * Will only create the view once even if window size changes, afterwards it is simply detached/reinserted.
 * Usage: *ifScreenSize="ScreenType.Mobile" or *ifScreenSize="ScreenType.Desktop"
 * */
export class IfScreenSizeDirective implements OnDestroy {
  resizeSubscription: Subscription;
  embeddedView: ViewRef | null;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private screenSizeService: ScreenSizeService
  ) {}

  @Input() set ifScreenSize(media: ScreenType) {
    this.resizeSubscription = this.screenSizeService
      .selectScreenWidth()
      .pipe(tap((width) => this.resizeCheck(width, media)))
      .subscribe();
  }

  /** Creates Embedded View */
  createView(): void {
    this.viewContainer.createEmbeddedView(this.templateRef);
    this.embeddedView = this.viewContainer.get(0);
  }

  /** On selected media, creates or display existing view.
   * Otherwise, detaches the view without destroying it.
   */
  resizeCheck(width: number, media: ScreenType): void {
    if (this.screenMatchesSelector(width, media)) {
      if (this.embeddedView) {
        this.viewContainer.insert(this.embeddedView);
      } else {
        this.createView();
      }
    } else {
      this.viewContainer.detach();
    }
  }

  /** Checks if the screen width matches the selected media option */
  screenMatchesSelector(width: number, media: ScreenType): boolean {
    const mobileBreakPoint = this.getDesktopBreakpointValue();

    const mobileCheck = media == ScreenType.Mobile && width < mobileBreakPoint;
    const desktopCheck = media == ScreenType.Desktop && width >= mobileBreakPoint;

    return mobileCheck || desktopCheck;
  }

  /** Get the desktop breakpoint from bootstrap variables defined as $grid-breakpoints */
  getDesktopBreakpointValue(): number {
    const bodyStyles = window.getComputedStyle(document.body);
    const mobileBreakpointSASSValue = bodyStyles.getPropertyValue('--bs-breakpoint-lg');
    const mobileBreakpointNumber = +mobileBreakpointSASSValue.replace('px', '');

    return mobileBreakpointNumber;
  }

  @HostListener('window:beforeunload')
  ngOnDestroy() {
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
  }
}
